import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api, type NutritionSummary } from "../../lib/api";
import { EmptyState } from "../../components/EmptyState";
import { fmtKcal, fmtNumber } from "../../lib/format";
import { DAY_LABEL, VERDICTS, VerdictPill } from "./_shared";

const RANGES = [14, 30, 90];

/** Good days and bad days over time, and whether the scale agrees. */
export default function NutritionHistory() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<NutritionSummary | null | "error">(null);

  useEffect(() => {
    setData(null);
    api.nutrition.summary(days).then(setData).catch(() => setData("error"));
  }, [days]);

  const d = data && data !== "error" ? data : null;
  if (!d) {
    return data === "error" ? <EmptyState error title="Couldn't load your history" /> : <div className="text-ink-subtle">Loading…</div>;
  }

  const chart = [...d.days].reverse().map((r) => ({
    date: r.date.slice(5),
    calories: r.calories || null,
    protein: r.protein_g,
    target: r.target,
    verdict: r.verdict,
  }));
  const s = d.summary;
  const loggedPct = Math.round((s.days_logged / s.days_tracked) * 100);

  return (
    <div className="animate-fade-in">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="section-title">Good days and bad</h2>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setDays(r)}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition duration-fast ${
                days === r ? "bg-accent/10 text-accent" : "text-ink-muted hover:text-ink"
              }`}
            >
              {r}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="On track" value={`${s.counts.good} days`} sub={`of ${s.days_logged} logged`} />
        <Stat label="Over target" value={`${s.counts.over} days`} />
        <Stat label="Current run" value={`${s.good_streak}`} sub="good days" />
        <Stat label="Logged" value={`${loggedPct}%`} sub={`${s.days_logged} of ${s.days_tracked} days`} />
      </div>

      <div className="card card-pad mt-4">
        <div className="caption mb-2">Calories a day against target</div>
        <p className="mb-2 text-xs text-ink-subtle">
          The line is today&apos;s target; each day is judged against its own, which moves with the
          training day your plan assigns.
        </p>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart} margin={{ top: 5, right: 5, bottom: 0, left: -18 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
              <XAxis dataKey="date" axisLine={false} tickLine={false} fontSize={11} minTickGap={20} />
              <YAxis axisLine={false} tickLine={false} fontSize={11} />
              <Tooltip
                formatter={(v: number, name: string) => [name === "calories" ? fmtKcal(v) : v, name === "calories" ? "Eaten" : name]}
                labelFormatter={(l) => `${l}`}
              />
              <ReferenceLine y={d.targets.calories} stroke="#9a3412" strokeDasharray="4 3" />
              <Bar dataKey="calories" radius={[2, 2, 0, 0]}>
                {chart.map((c, i) => (
                  <Cell key={i} fill={VERDICTS[c.verdict].colour} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-2 flex flex-wrap gap-3 text-[10px] uppercase tracking-wider text-ink-subtle">
          {(["good", "over", "under", "partial"] as const).map((k) => (
            <span key={k} className="flex items-center gap-1">
              <span className="inline-block h-2 w-2 rounded-sm" style={{ background: VERDICTS[k].colour }} />
              {VERDICTS[k].label}
            </span>
          ))}
        </div>
      </div>

      <div className="card card-pad mt-4">
        <div className="caption mb-2">Protein a day</div>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chart} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
              <XAxis dataKey="date" axisLine={false} tickLine={false} fontSize={11} minTickGap={24} />
              <YAxis axisLine={false} tickLine={false} fontSize={11} />
              <Tooltip formatter={(v: number) => [`${v} g`, "Protein"]} />
              <ReferenceLine y={d.targets.protein_g} stroke="#5e7a73" strokeDasharray="4 3" />
              <Line type="monotone" dataKey="protein" stroke="#5e7a73" strokeWidth={2} dot={false} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card card-pad mt-4">
        <div className="caption">Does the scale agree?</div>
        <div className="mt-2 grid grid-cols-3 gap-2">
          <Stat label="Avg eaten" value={s.avg_calories != null ? fmtKcal(s.avg_calories) : "—"} sub="on logged days" />
          <Stat
            label="Avg balance"
            value={s.avg_balance != null ? `${s.avg_balance > 0 ? "+" : ""}${fmtKcal(s.avg_balance)}` : "—"}
            sub="against what you burn"
          />
          <Stat
            label="Predicted"
            value={s.predicted_kg != null ? `${s.predicted_kg > 0 ? "+" : ""}${s.predicted_kg} kg` : "—"}
            sub={s.actual_kg != null ? `scale says ${s.actual_kg > 0 ? "+" : ""}${s.actual_kg} kg` : "no weigh-ins"}
          />
        </div>
        <p className="mt-2 text-xs text-ink-subtle">
          The prediction only counts days you logged, so it reads low if the record has gaps. When
          the two disagree badly, the logging is usually the part that is wrong.
        </p>
      </div>

      <h3 className="section-title mt-8 mb-3">Day by day</h3>
      <div className="card overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Eaten</th>
              <th>Protein</th>
              <th>Day type</th>
              <th>Steps</th>
              <th>Day</th>
            </tr>
          </thead>
          <tbody>
            {d.days.slice(0, 21).map((r) => (
              <tr key={r.date}>
                <td className="whitespace-nowrap">{r.date.slice(5)}</td>
                <td className="tabular-nums">{r.entries ? fmtKcal(r.calories) : <span className="muted">—</span>}</td>
                <td className="tabular-nums">{r.protein_g != null ? `${fmtNumber(r.protein_g, 0)}g` : <span className="muted">—</span>}</td>
                <td className="capitalize text-ink-subtle">
                  {DAY_LABEL[r.day_type]}
                  {!r.day_type_set && <span className="muted"> ?</span>}
                </td>
                <td className="tabular-nums">{r.steps != null ? fmtNumber(r.steps, 0) : <span className="muted">—</span>}</td>
                <td>
                  <VerdictPill verdict={r.verdict} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-md border border-ink-line bg-canvas px-2 py-2 text-center">
      <div className="text-[10px] uppercase tracking-wider text-ink-subtle">{label}</div>
      <div className="mt-0.5 font-display tabular-nums text-ink">{value}</div>
      {sub && <div className="mt-0.5 text-[10px] text-ink-subtle">{sub}</div>}
    </div>
  );
}
